import { PlaylistTrack, TrackRecommendation } from '@/types/playlist'

// 自动根据当前访问的主机名设置 API URL
// 在开发环境中，使用相对路径让 Vite 代理处理
// 在生产环境中，使用完整的 URL
const getApiBaseUrl = () => {
  // 如果设置了环境变量，优先使用
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  
  // 在开发模式下，使用相对路径（通过 Vite 代理）
  if (import.meta.env.DEV) {
    return '' // 空字符串表示使用相对路径，会被 Vite 代理处理
  }
  
  // 生产模式：使用当前主机名
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const port = '3001' // 后端端口
  return `${protocol}//${hostname}:${port}`
}

const API_BASE_URL = getApiBaseUrl()

// 导出函数供其他模块使用
export { getApiBaseUrl }

// Stash 相关接口
export interface StashInfo {
  stashes: string[]
  currentStash: string
  count: number
}

export interface SetStashResponse {
  success: boolean
  currentStash: string
  message: string
}

export interface TracksResponse {
  tracks: PlaylistTrack[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export interface SearchResponse {
  tracks: PlaylistTrack[]
  query: string
  count: number
}

export interface ArtistsResponse {
  artists: string[]
  count: number
}

export interface RecommendationsResponse {
  referenceTrack: PlaylistTrack
  recommendations: PlaylistTrack[]
  count: number
}

export interface RecommendationsByDistanceResponse {
  referenceTrack: PlaylistTrack
  recommendations: {
    close: TrackRecommendation[]
    medium: TrackRecommendation[]
    distant: TrackRecommendation[]
  }
}

// ==================== Stash API ====================

// 获取可用的 stash 列表
export async function getAvailableStashes(): Promise<StashInfo> {
  const response = await fetch(`${API_BASE_URL}/api/stashes`)
  if (!response.ok) {
    throw new Error('Failed to fetch stashes')
  }
  return response.json()
}

// 设置当前使用的 stash
export async function setCurrentStash(stashName: string): Promise<SetStashResponse> {
  const response = await fetch(`${API_BASE_URL}/api/stashes/set`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ stashName })
  })
  if (!response.ok) {
    throw new Error('Failed to set stash')
  }
  return response.json()
}

// ==================== Tracks API ====================

// 获取所有歌曲（带分页）
export async function getAllTracks(limit: number = 100, offset: number = 0): Promise<TracksResponse> {
  const response = await fetch(`${API_BASE_URL}/api/tracks?limit=${limit}&offset=${offset}`)
  if (!response.ok) {
    throw new Error('Failed to fetch tracks')
  }
  return response.json()
}

// 获取随机歌曲
export async function getRandomTracks(limit: number = 100): Promise<SearchResponse> {
  const response = await fetch(`${API_BASE_URL}/api/tracks/random?limit=${limit}`)
  if (!response.ok) {
    throw new Error('Failed to fetch random tracks')
  }
  return response.json()
}

// 根据ID获取单个歌曲
export async function getTrackById(id: string): Promise<PlaylistTrack> {
  const response = await fetch(`${API_BASE_URL}/api/tracks/${id}`)
  if (!response.ok) {
    throw new Error('Failed to fetch track')
  }
  return response.json()
}

// 搜索歌曲
export async function searchTracks(query: string, limit: number = 50): Promise<SearchResponse> {
  const response = await fetch(`${API_BASE_URL}/api/tracks/search?q=${encodeURIComponent(query)}&limit=${limit}`)
  if (!response.ok) {
    throw new Error('Failed to search tracks')
  }
  return response.json()
}

// 根据BPM范围搜索
export async function searchTracksByBpm(minBpm: number, maxBpm: number, limit: number = 50): Promise<SearchResponse> {
  const response = await fetch(`${API_BASE_URL}/api/tracks/bpm/${minBpm}/${maxBpm}?limit=${limit}`)
  if (!response.ok) {
    throw new Error('Failed to search tracks by BPM')
  }
  return response.json()
}

// 根据调性搜索
export async function searchTracksByKey(key: string, limit: number = 50): Promise<SearchResponse> {
  const response = await fetch(`${API_BASE_URL}/api/tracks/key/${encodeURIComponent(key)}?limit=${limit}`)
  if (!response.ok) {
    throw new Error('Failed to search tracks by key')
  }
  return response.json()
}

// 获取歌手列表
export async function getArtists(limit: number = 100): Promise<ArtistsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/artists?limit=${limit}`)
  if (!response.ok) {
    throw new Error('Failed to fetch artists')
  }
  return response.json()
}

// 根据歌手获取歌曲
export async function getTracksByArtist(artistName: string, limit: number = 100): Promise<SearchResponse> {
  const response = await fetch(`${API_BASE_URL}/api/artists/${encodeURIComponent(artistName)}/tracks?limit=${limit}`)
  if (!response.ok) {
    throw new Error('Failed to fetch tracks by artist')
  }
  return response.json()
}

// 获取推荐歌曲
export async function getRecommendedTracks(trackId: string, limit: number = 20): Promise<RecommendationsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/tracks/${trackId}/recommendations?limit=${limit}`)
  if (!response.ok) {
    throw new Error('Failed to fetch recommendations')
  }
  return response.json()
}

// 按距离分级获取推荐歌曲
export async function getRecommendationsByDistance(
  trackId: string, 
  excludeIds: string[] = []
): Promise<RecommendationsByDistanceResponse> {
  const excludeParam = excludeIds.length > 0 ? `?exclude=${excludeIds.join(',')}` : ''
  const response = await fetch(`${API_BASE_URL}/api/tracks/${trackId}/recommendations-by-distance${excludeParam}`)
  if (!response.ok) {
    throw new Error('Failed to fetch recommendations by distance')
  }
  return response.json()
}

